import type { SearchFilters } from './schemas'
import type { GeoCircle } from '~/utility/geo/GeoLocation'

/**
 * If filters have `q` but no `area` (lat/lng), resolve the query text
 * to a geo-point via the Mapbox geocode_mode endpoint.
 *
 * Returns a new filters object with `area` and `placename` set and `q`
 * cleared. Returns the original filters unchanged if no resolution needed
 * or if geocoding fails.
 */
export async function resolveQueryToGeoPoint(
  filters: SearchFilters,
  langCode: string,
): Promise<SearchFilters> {
  const q = filters.q
  if (!q || (filters.area && !filters.area.isEmptyPoint)) return filters

  const sessionToken = generateSessionToken()

  try {
    const suggestResponse = await $fetch<{
      suggestions: Array<{
        name: string
        mapbox_id: string
        place_formatted?: string
      }>
    }>('/api/mb/suggest/', {
      params: {
        q,
        geocode_mode: '1',
        language: langCode,
        session_token: sessionToken,
      },
      timeout: 5000,
    })

    const firstSuggestion = suggestResponse?.suggestions?.[0]
    if (!firstSuggestion?.mapbox_id) return filters

    const retrieveResponse = await $fetch<{
      properties: {
        coordinates: {
          latitude: number
          longitude: number
        }
      }
    }>(`/api/mb/retrieve/${firstSuggestion.mapbox_id}`, {
      params: {
        session_token: sessionToken,
      },
      timeout: 5000,
    })

    const coords = retrieveResponse?.properties?.coordinates
    if (coords?.latitude == null || coords?.longitude == null) return filters

    const area: GeoCircle = {
      lat: coords.latitude,
      lng: coords.longitude,
      isEmptyPoint: false,
    }

    return {
      ...filters,
      area,
      placename: firstSuggestion.name,
      q: undefined,
    }
  } catch (error) {
    console.warn(
      'resolveQueryToGeoPoint: geocode failed, continuing without coordinates',
      error,
    )
    return filters
  }
}

function generateSessionToken(): string {
  return crypto.randomUUID()
}
